import { sendAuthRequest } from '../api';
import { dashboardApi } from '../dashboard-api';
import { TTS_SPEED } from 'src/pages/solve-config/voice/constants';
import {
  AcknowledgedResponse,
  ApiConfiguration,
  ApiConfigurationResponse,
  BuyPhoneNumberResponse,
  EmailConfiguration,
  GetAvailableVoicesResponse,
  GetBoughtPhoneNumbersResponse,
  GetPhoneNumbersResponse,
  GetSipDomainsResponse,
  KnowledgeConfiguration,
  PhoneNumberProvider,
  PhoneNumberSearchType,
  PlaybookConfiguration,
  PlaybookConfigurationResponse,
  SlackConfiguration,
  SlackConfigurationResponse,
  SolveKnowledgeSource,
  VoiceConfiguration,
  VoiceConfigurationResponse,
  WebCallConfigData,
  WebCallConfigurationResponse,
  WidgetConfiguration,
  WidgetConfigurationResponse,
  WidgetConfigurationsResponse,
} from 'src/slices/solve-config/types';
import { normalizeResponseErrors } from 'src/utils/normalizeResponse';

const BASE_URL = `${API_URL}dashboard-controls/solve/config`;

export const getInitialSolveConfiguration =
  (): Promise<AcknowledgedResponse> => {
    return sendAuthRequest(`${BASE_URL}/initialize`, {}, 'PUT')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

/**
 * widget configuration endpoints
 */

export const getWidgetConfiguration =
  (): Promise<WidgetConfigurationResponse> => {
    return sendAuthRequest(`${BASE_URL}/widget`, null, 'GET')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const getPlaybookConfiguration =
  (): Promise<PlaybookConfigurationResponse> => {
    return sendAuthRequest(`${BASE_URL}/playbook`, null, 'GET')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const getMultibrandWidgetConfiguration = ({
  brandId,
}: {
  brandId: string;
}): Promise<WidgetConfigurationResponse> => {
  return sendAuthRequest(`${BASE_URL}/brand/${brandId}/widget`, null, 'GET')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const getKnowledgeConfiguration =
  (): Promise<KnowledgeConfiguration> => {
    return sendAuthRequest(`${BASE_URL}/knowledge`, null, 'GET')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const updateWidgetConfiguration = (
  updatedConfigurations: WidgetConfiguration,
): Promise<WidgetConfiguration> => {
  return sendAuthRequest(`${BASE_URL}/widget`, updatedConfigurations, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updatePlaybookConfiguration = (
  updatedConfigurations: PlaybookConfiguration,
): Promise<PlaybookConfiguration> => {
  return sendAuthRequest(`${BASE_URL}/playbook`, updatedConfigurations, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updateEmailConfiguration = (
  updatedConfigurations: EmailConfiguration,
): Promise<EmailConfiguration> => {
  return sendAuthRequest(`${BASE_URL}/email`, updatedConfigurations, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updateMultibrandWidgetConfiguration = (
  updatedConfigurations: WidgetConfiguration,
  {
    brandId,
  }: {
    brandId: string;
  },
): Promise<WidgetConfiguration> => {
  return sendAuthRequest(
    `${BASE_URL}/brand/${brandId}/widget`,
    updatedConfigurations,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const activateWidget = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/widget/activate`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const activateMultibrandWidget = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/brand/${brandId}/widget/activate`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const deactivateWidget = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/widget/deactivate`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const deactivateMultibrandWidget = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/brand/${brandId}/widget/deactivate`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/publish`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishWidgetConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/widget/publish`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardWidgetConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/widget/discard`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardMultibrandWidgetConfiguration = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/brand/${brandId}/widget/discard`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishPlaybookConfiguration =
  (): Promise<AcknowledgedResponse> => {
    return sendAuthRequest(`${BASE_URL}/playbook/publish`, null, 'PUT')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const discardPlaybookConfiguration =
  (): Promise<AcknowledgedResponse> => {
    return sendAuthRequest(`${BASE_URL}/playbook/discard`, null, 'PUT')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

/**
 * slack configuration endpoints
 */

export const getSlackConfiguration =
  (): Promise<SlackConfigurationResponse> => {
    return sendAuthRequest(`${BASE_URL}/slack`, null, 'GET')
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const updateSlackConfiguration = (
  updatedConfigurations: SlackConfiguration,
): Promise<SlackConfiguration> => {
  return sendAuthRequest(`${BASE_URL}/slack`, updatedConfigurations, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishSlackConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/slack/publish`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardSlackConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/slack/discard`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

/**
 * api configuration endpoints
 */

export const getApiConfiguration = (): Promise<ApiConfigurationResponse> => {
  return sendAuthRequest(`${BASE_URL}/solve-api`, null, 'GET')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updateApiConfiguration = (
  updatedConfigurations: ApiConfiguration,
): Promise<ApiConfiguration> => {
  return sendAuthRequest(`${BASE_URL}/solve-api`, updatedConfigurations, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishApiConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/solve-api/publish`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardApiConfiguration = (): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(`${BASE_URL}/solve-api/discard`, null, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishMultibrandWidgetConfiguration = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/brand/${brandId}/widget/publish`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishVoiceConfiguration = ({
  phoneNumber,
}: {
  phoneNumber: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/publish/${phoneNumber}`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardVoiceConfiguration = ({
  phoneNumber,
}: {
  phoneNumber: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/discard/${phoneNumber}`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updateKnowledgeConfiguration = (
  updatedConfigurations: SolveKnowledgeSource,
): Promise<KnowledgeConfiguration> => {
  return sendAuthRequest(`${BASE_URL}/knowledge`, updatedConfigurations, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

/**
 * Voice configuration endpoints
 */
export const updateVoiceConfiguration = ({
  updatedConfigurations,
}: {
  updatedConfigurations: VoiceConfiguration;
}): Promise<VoiceConfiguration> => {
  const languageConfig = updatedConfigurations.language_config;
  const request = {
    ...updatedConfigurations,
    ...(languageConfig
      ? {
          // The voice update endpoint still persists these legacy fields.
          // Keep them in sync with the nested config until the endpoint fully
          // migrates to language_config.
          default_language: languageConfig.default_language,
          language_voice_overrides: languageConfig.language_voice_overrides,
          use_only_default_language: !languageConfig.multilingual_enabled,
        }
      : {}),
  };

  return sendAuthRequest(`${BASE_URL}/voice`, request, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const getAvailablePhoneNumbers = ({
  contains,
  countryCode,
  searchType = 'contains',
}: {
  contains: string;
  countryCode: string;
  searchType?: PhoneNumberSearchType;
}): Promise<GetPhoneNumbersResponse> => {
  const params = new URLSearchParams({ country: countryCode });
  if (contains) {
    params.set('contains', contains);
    params.set('search_type', searchType);
  }

  return sendAuthRequest(
    `${BASE_URL}/voice/available-phone-numbers?${params.toString()}`,
    null,
    'GET',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const getBoughtPhoneNumbers =
  (): Promise<GetBoughtPhoneNumbersResponse> => {
    return sendAuthRequest(
      `${BASE_URL}/voice/bought-phone-numbers`,
      null,
      'GET',
    )
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      .then((res: Response) => res.json());
  };

export const getAvailableVoices = ({
  filterByLanguage,
  language,
}: {
  language?: string;
  filterByLanguage?: boolean;
}): Promise<GetAvailableVoicesResponse> => {
  const params = new URLSearchParams();
  if (filterByLanguage !== undefined) {
    params.set('filter_by_language', String(filterByLanguage));
  }
  const query = params.toString();

  return sendAuthRequest(
    `${BASE_URL}/voice/available-voices/${language}${query ? `?${query}` : ''}`,
    null,
    'GET',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const getCustomerPhoneNumbers = (): Promise<GetPhoneNumbersResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/customer-phone-numbers`,
    null,
    'GET',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const importPhoneNumber = ({
  brandId,
  phoneNumber,
}: {
  brandId: string;
  phoneNumber: string;
}): Promise<BuyPhoneNumberResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/import-phone-number`,
    {
      brand_id: brandId,
      phone_number: phoneNumber,
    },
    'POST',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const getSipDomains = (): Promise<GetSipDomainsResponse> => {
  return sendAuthRequest(`${BASE_URL}/voice/sip-domains`, null, 'GET')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const createSipDomain = ({
  brandId,
  friendlyName,
  sipUri,
}: {
  brandId: string;
  friendlyName: string;
  sipUri: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/create-sip-domain`,
    {
      brand_id: brandId,
      friendly_name: friendlyName,
      sip_uri: sipUri,
    },
    'POST',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const assignSipDomain = ({
  brandId,
  domainSid,
  sipDomain,
}: {
  brandId: string;
  domainSid: string;
  sipDomain: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/assign-sip-domain`,
    {
      brand_id: brandId,
      domain_sid: domainSid,
      sip_domain: sipDomain,
    },
    'POST',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const unassignSipDomain = ({
  brandId,
  sipDomain,
}: {
  brandId: string;
  sipDomain: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/unassign-sip-domain`,
    {
      brand_id: brandId,
      sip_domain: sipDomain,
    },
    'POST',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const buyPhoneNumber = ({
  brandId,
  countryCode,
  phoneNumber,
}: {
  brandId: string;
  countryCode: string;
  phoneNumber: string;
}): Promise<BuyPhoneNumberResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/buy-phone-number`,
    {
      brand_id: brandId,
      country_code: countryCode,
      phone_number: phoneNumber,
    },
    'POST',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const releasePhoneNumber = ({
  phoneNumber,
  provider,
}: {
  phoneNumber: string;
  provider: PhoneNumberProvider;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/release-phone-number/${provider}/${phoneNumber}`,
    null,
    'DELETE',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const getVoiceConfigurationByPhoneNumber = ({
  phoneNumber,
}: {
  phoneNumber: string;
}): Promise<VoiceConfigurationResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/phone-number/${phoneNumber}`,
    null,
    'GET',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const solveVoiceTTSBytes = ({
  language,
  speed,
  transcript,
  voice_id,
}: {
  language: string;
  speed: TTS_SPEED;
  transcript: string;
  voice_id: string;
}): Promise<Response> => {
  return (
    sendAuthRequest(
      `${BASE_URL}/voice/tts/bytes`,
      { language, speed, transcript, voice_id },
      'POST',
    )
      .then((res: Response | { error: string }) => normalizeResponseErrors(res))
      // tts returns audio in bytes, just return as is
      .then((res: Response) => res)
  );
};

export const getVoiceConfigurationByBrand = ({
  brandId,
}: {
  brandId: string;
}): Promise<VoiceConfigurationResponse> => {
  return sendAuthRequest(`${BASE_URL}/voice/brand/${brandId}`, null, 'GET')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishVoiceConfigurationByBrand = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/publish/brand/${brandId}`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardVoiceConfigurationByBrand = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/voice/discard/brand/${brandId}`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

/**
 * Web call configuration endpoints
 */
export const getWebCallConfigurationByBrand = ({
  brandId,
}: {
  brandId: string;
}): Promise<WebCallConfigurationResponse> => {
  return sendAuthRequest(`${BASE_URL}/web-call/brand/${brandId}`, null, 'GET')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const updateWebCallConfiguration = (
  data: WebCallConfigData,
): Promise<WebCallConfigData> => {
  const request = { ...data };
  delete request.default_language;
  delete request.use_only_default_language;

  return sendAuthRequest(`${BASE_URL}/web-call`, request, 'PUT')
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const publishWebCallConfigurationByBrand = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/web-call/publish/brand/${brandId}`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

export const discardWebCallConfigurationByBrand = ({
  brandId,
}: {
  brandId: string;
}): Promise<AcknowledgedResponse> => {
  return sendAuthRequest(
    `${BASE_URL}/web-call/discard/brand/${brandId}`,
    null,
    'PUT',
  )
    .then((res: Response | { error: string }) => normalizeResponseErrors(res))
    .then((res: Response) => res.json());
};

/**
 * RTK query for brands
 */
const solveConfigApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    duplicateVoiceConfigurationForBrand: builder.mutation<
      VoiceConfiguration,
      { brandId: string; phone_numbers: string[] }
    >({
      invalidatesTags: ['Brands'],
      query: ({ brandId, phone_numbers }) => ({
        body: { phone_numbers },
        method: 'POST',
        url: `${BASE_URL}/brand/${brandId}/voice/duplicate`,
      }),
    }),
    duplicateWebCallConfigurationForBrand: builder.mutation<
      Record<string, unknown>,
      { brandId: string }
    >({
      invalidatesTags: ['Brands'],
      query: ({ brandId }) => ({
        method: 'POST',
        url: `${BASE_URL}/brand/${brandId}/web-call/duplicate`,
      }),
    }),
    duplicateWidgetConfigurationForBrand: builder.mutation<
      WidgetConfiguration,
      { brandId: string }
    >({
      invalidatesTags: ['Brands'],
      query: ({ brandId }) => ({
        method: 'POST',
        url: `${BASE_URL}/brand/${brandId}/widget/duplicate`,
      }),
    }),
    getPublishedVoiceConfigurations: builder.query<
      VoiceConfigurationResponse,
      void
    >({
      query: () => ({
        method: 'GET',
        url: `${BASE_URL}/voice/published`,
      }),
    }),
    getPublishedWidgetConfigurations: builder.query<
      WidgetConfigurationsResponse,
      void
    >({
      query: () => ({
        method: 'GET',
        url: `${BASE_URL}/widget/published`,
      }),
    }),
    toneOfVoiceTest: builder.query<
      { response: string },
      { channel?: string; input: string }
    >({
      query: ({ channel, input }) => ({
        body: { input },
        method: 'POST',
        url: `${BASE_URL}/widget/tone-of-voice-test${
          channel === 'email' ? '?is_email=true' : ''
        }`,
      }),
    }),
  }),
});

export const {
  useDuplicateVoiceConfigurationForBrandMutation,
  useDuplicateWebCallConfigurationForBrandMutation,
  useDuplicateWidgetConfigurationForBrandMutation,
  useGetPublishedVoiceConfigurationsQuery,
  useGetPublishedWidgetConfigurationsQuery,
  useToneOfVoiceTestQuery,
} = solveConfigApi;
